"use client";
import { useCallback } from "react"

// --Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --Lib ---
import { isExtensionAvailable } from "@/tiptap/lib/tiptap-utils"
import { getTable } from "@/tiptap/nodes/table-node/lib/tiptap-table-utils"

// --Icons ---
import { MoveHorizontalIcon } from "@/tiptap/icons/move-horizontal-icon"

/**
 * Required Tiptap extensions for this feature to work.
 * - `table` to target the node and update attributes
 * - `tableHandleExtension` (or your table controls) to ensure table tooling is enabled
 */
const REQUIRED_EXTENSIONS = ["table", "tableHandleExtension"]

function removeStyleProperties(style, properties) {
  if (!style) return null

  const propertiesToRemove = new Set(properties.map((property) => property.toLowerCase()))
  const nextStyle = style
    .split(";")
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const [property] = declaration.split(":")
      return property && !propertiesToRemove.has(property.trim().toLowerCase())
    })
    .join("; ")

  return nextStyle ? `${nextStyle};` : null
}

/**
 * Returns whether a "fit to width" action can run in the current state.
 * Checks: editor presence, editability, required extensions,
 * and that the selection is somewhere inside a table.
 */
function canFitTableToWidth(editor) {
  if (
    !editor ||
    !editor.isEditable ||
    !isExtensionAvailable(editor, REQUIRED_EXTENSIONS)
  ) {
    return false
  }

  try {
    return (editor.isActive("table") ||
    editor.isActive("tableCell") || editor.isActive("tableHeader"));
  } catch {
    return false
  }
}

/**
 * Clears stored pixel/percent widths on `table` only so it lays out like a
 * newly inserted table (fluid 100% in the editor and published view).
 * Nested tables inside cells are never visited.
 */
export function applyFitWidthsToTableCells(tr, table) {
  const seenCellPositions = new Set()

  table.map.map.forEach((relativeCellPos) => {
    if (seenCellPositions.has(relativeCellPos)) return
    seenCellPositions.add(relativeCellPos)

    const cell = table.node.nodeAt(relativeCellPos)
    if (
      !cell ||
      (cell.type.name !== "tableCell" && cell.type.name !== "tableHeader")
    ) {
      return
    }

    if (cell.attrs.colwidth == null && cell.attrs.width == null) return

    tr.setNodeMarkup(table.start + relativeCellPos, undefined, {
      ...cell.attrs,
      colwidth: null,
      width: null,
    })
  })

  const nextTableStyle = removeStyleProperties(table.node.attrs.style, [
    "width",
    "min-width",
  ])
  if (nextTableStyle !== table.node.attrs.style || table.node.attrs.width != null) {
    tr.setNodeMarkup(table.pos, undefined, {
      ...table.node.attrs,
      style: nextTableStyle,
      width: null,
    })
  }

  return tr
}

/**
 * Restores the selected table to the same fluid layout as a newly inserted
 * table: no stored colwidth, no baked table/col pixel widths.
 *
 * @param editor - The ProseMirror editor instance, or null
 * @returns true if the table width was successfully set, false otherwise
 */
export function setTableAutoWidth(editor) {
  if (!canFitTableToWidth(editor) || !editor) return false

  try {
    const table = getTable(editor)
    if (!table) return false

    const tr = applyFitWidthsToTableCells(editor.state.tr, table)

    if (tr.docChanged) {
      editor.view.dispatch(tr)
    }

    return true
  } catch (error) {
    console.error("Error setting table auto width:", error)
    return false
  }
}

/**
 * Executes the "fit to width" operation. Safely no-ops if unavailable.
 * Returns `true` on success, `false` otherwise.
 */
function tableFitToWidth({
  editor
}) {
  if (!canFitTableToWidth(editor) || !editor) {
    return false
  }

  try {
    return setTableAutoWidth(editor);
  } catch (error) {
    console.error("Error adjusting table width:", error)
    return false
  }
}

/**
 * Determines whether a UI control should be visible based on the editor
 * state and `hideWhenUnavailable` setting.
 */
function shouldShowButton(
  {
    editor,
    hideWhenUnavailable
  }
) {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false

  return hideWhenUnavailable ? canFitTableToWidth(editor) : true;
}

/**
 * React hook that provides a **Fit table to container width** action for Tiptap.
 *
 * What it does:
 * - Clears stored column/table widths so the table is fluid again
 *   (same as a newly inserted table: 100% of the article/editor)
 *
 * Returned API:
 * - `isVisible`: whether a button should be shown
 * - `canFitToWidth`: whether the action can execute now
 * - `handleFitToWidth()`: runs the action; returns `true` on success
 * - `label`: UI label, e.g. "Fit to width"
 * - `Icon`: a presentational icon component
 */
export function useTableFitToWidth(config = {}) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onWidthAdjusted,
  } = config

  const { editor } = useTiptapEditor(providedEditor)

  const isVisible = shouldShowButton({
    editor,
    hideWhenUnavailable,
  })

  const canPerformAction = canFitTableToWidth(editor)

  const handleFitToWidth = useCallback(() => {
    const success = tableFitToWidth({ editor })
    if (success) onWidthAdjusted?.()
    return success
  }, [editor, onWidthAdjusted])

  const label = "Fit to width"
  const Icon = MoveHorizontalIcon

  return {
    isVisible,
    canFitToWidth: canPerformAction,
    handleFitToWidth,
    label,
    Icon,
  }
}
